import React, { useEffect, useState } from "react";
import { X, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

export type ToastType = "success" | "warning" | "critical";

export interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
}

interface ToastProps extends ToastData {
  onClose: () => void;
}

const EXIT_DURATION = 260;

const toastTheme: Record<
  ToastType,
  {
    background: string;
    accent: string;
    progress: string;
  }
> = {
  success: {
    background: "linear-gradient(135deg, #1f5d45 0%, #328362 48%, #57c38f 100%)",
    accent: "rgba(198, 244, 223, 0.72)",
    progress: "#e8fff4",
  },
  warning: {
    background: "linear-gradient(135deg, #744514 0%, #a7671d 48%, #df9a3b 100%)",
    accent: "rgba(255, 236, 204, 0.72)",
    progress: "#fff6df",
  },
  critical: {
    background: "linear-gradient(135deg, #692427 0%, #96373b 48%, #d45a61 100%)",
    accent: "rgba(255, 219, 221, 0.72)",
    progress: "#fff0f1",
  },
};

function PaperPlaneArt({ accent, type }: { accent: string; type: ToastType }) {
  const isError = type === "critical";

  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute bottom-[18px] right-[44px]"
      width="130"
      height="96"
      viewBox={isError ? "0 0 420 270" : "0 0 420 268"}
      fill="none"
    >
      {isError ? (
        <>
          <path
            d="M23 210C43 158 64 140 96 152C126 163 136 204 168 196C201 188 181 128 168 128C153 128 146 169 170 193C198 219 247 190 305 156C335 138 361 118 387 215"
            stroke={accent}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeDasharray="3 8"
          />
          <path
            d="M415.486 231.388C414.43 232.528 412.903 232.971 411.401 232.572L320.07 208.289C318.596 207.897 317.496 206.78 317.128 205.3C316.757 203.819 317.204 202.316 318.319 201.277L340.913 180.247L337.559 158.177C337.431 157.342 337.838 156.556 338.592 156.175C338.872 156.034 339.172 155.963 339.466 155.963C339.796 155.963 340.119 156.053 340.409 156.221H340.413L357.537 164.77L380.811 143.106C381.584 142.386 382.587 142 383.616 142C383.948 142 384.282 142.04 384.61 142.122C385.968 142.458 387.052 143.446 387.514 144.765L416.351 227.216C416.864 228.685 416.54 230.242 415.484 231.384L415.486 231.388ZM339.107 157.199C338.867 157.321 338.63 157.597 338.691 158.005L341.929 179.303L351.72 170.189L339.993 157.337C339.703 157.052 339.346 157.079 339.105 157.199H339.107ZM342.45 180.384L399.198 216.834L352.515 171.014L342.45 180.382V180.384ZM342.778 158.687L352.56 169.408L356.642 165.609L342.778 158.687ZM415.272 227.6L386.435 145.149C386.101 144.196 385.319 143.482 384.339 143.239C383.36 142.997 382.333 143.262 381.594 143.95L353.357 170.233L403.974 219.915C404.184 220.121 404.203 220.451 404.022 220.681C403.839 220.91 403.512 220.965 403.264 220.805L341.585 181.188L319.1 202.118C318.294 202.868 317.972 203.953 318.239 205.023C318.506 206.092 319.3 206.898 320.364 207.181L411.695 231.464C412.779 231.753 413.882 231.434 414.646 230.609C415.41 229.783 415.643 228.658 415.272 227.598V227.6Z"
            fill="white"
          />
          <path
            d="M314 150.431C314.852 150.219 317.558 147.868 319.5 147.002C320.806 146.42 324.5 145.5 327.854 147.002C330.733 148.292 331.837 148.66 333.5 150C334.629 151.555 336.233 153.698 336.5 154.5"
            stroke="white"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeDasharray="4 4"
          />
        </>
      ) : (
        <>
          <path
            d="M23 210C43 158 64 140 96 152C126 163 136 204 168 196C201 188 181 128 168 128C153 128 146 169 170 193C198 219 247 190 305 156C335 138 361 118 387 85"
            stroke={accent}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeDasharray="3 8"
          />
          <path
            d="M396.144 71.5185C395.088 70.3782 393.561 69.9351 392.059 70.3343L300.728 94.6175C299.254 95.0091 298.154 96.1264 297.786 97.6066C297.416 99.0868 297.862 100.59 298.977 101.629L321.571 122.66L318.217 144.729C318.089 145.564 318.496 146.351 319.25 146.731C319.53 146.872 319.83 146.943 320.124 146.943C320.454 146.943 320.777 146.853 321.067 146.685H321.071L338.196 138.136L361.469 159.801C362.243 160.521 363.245 160.906 364.274 160.906C364.606 160.906 364.94 160.866 365.268 160.784C366.626 160.448 367.71 159.461 368.172 158.141L397.009 75.6898C397.523 74.2211 397.198 72.6645 396.142 71.5223L396.144 71.5185ZM319.765 145.707C319.525 145.585 319.288 145.31 319.349 144.901L322.587 123.603L332.379 132.717L320.651 145.57C320.361 145.854 320.004 145.827 319.763 145.707H319.765ZM323.108 122.522L379.856 86.0724L333.173 131.892L323.108 122.524V122.522ZM323.436 144.219L333.219 133.499L337.3 137.297L323.436 144.219ZM395.93 75.3059L367.094 157.757C366.759 158.71 365.977 159.424 364.997 159.667C364.018 159.909 362.991 159.644 362.252 158.956L334.015 132.673L384.632 82.9916C384.842 82.7853 384.861 82.4549 384.68 82.2257C384.497 81.9965 384.17 81.9411 383.922 82.1016L322.243 121.718L299.758 100.789C298.952 100.038 298.63 98.9531 298.897 97.8836C299.164 96.814 299.958 96.008 301.022 95.7253L392.353 71.4421C393.437 71.1537 394.541 71.4726 395.304 72.2977C396.068 73.1228 396.301 74.2478 395.93 75.3078V75.3059Z"
            fill="white"
          />
        </>
      )}
    </svg>
  );
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  onClose,
  duration = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const theme = toastTheme[type];

  useEffect(() => {
    if (duration === 0) return;

    const timer = window.setTimeout(() => {
      setIsVisible(false);
      window.setTimeout(() => onClose(), EXIT_DURATION);
    }, duration);

    return () => window.clearTimeout(timer);
  }, [duration, onClose]);

  const closeToast = () => {
    setIsVisible(false);
    window.setTimeout(() => onClose(), EXIT_DURATION);
  };

  return (
    <div
      role="status"
      aria-live={type === "critical" ? "assertive" : "polite"}
      className={`relative min-h-[132px] w-[min(420px,calc(100vw-24px))] overflow-hidden rounded-md px-5 pb-5 pt-5 text-white shadow-[0_18px_40px_rgba(15,23,42,0.24)] transition-all duration-300 ${
        isVisible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
      }`}
      style={{ background: theme.background }}
    >
      <PaperPlaneArt accent={theme.accent} type={type} />

      <div className="relative z-10 flex items-start justify-between gap-4">
        <div className="min-w-0 pr-5 flex items-start gap-2.5">
          {type === "success" ? (
            <CheckCircle2 className="size-5 flex-shrink-0 text-white mt-px" strokeWidth={2.25} />
          ) : type === "warning" ? (
            <AlertTriangle className="size-5 flex-shrink-0 text-white mt-px" strokeWidth={2.25} />
          ) : (
            <XCircle className="size-5 flex-shrink-0 text-white mt-px" strokeWidth={2.25} />
          )}
          <div className="min-w-0">
            <h3 className="mb-3 text-[13px] font-bold leading-none tracking-normal text-white">
              {title}
            </h3>
            <p className="max-w-[280px] text-[13px] font-semibold leading-5 tracking-normal text-white/95">
              {message}
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="Close notification"
        onClick={closeToast}
        className="absolute top-4 right-4 z-20 grid h-8 w-8 place-items-center rounded-full text-white transition hover:bg-white/12 focus:outline-none focus:ring-2 focus:ring-white/55"
      >
        <X size={21} strokeWidth={2} />
      </button>

      <div className="absolute inset-x-0 bottom-[11px] h-[6px] bg-white/35">
        <div
          className="h-full rounded-r-full"
          style={{
            width: duration === 0 ? "100%" : "0%",
            background: theme.progress,
            animation: duration === 0 ? undefined : `toast-progress ${duration}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
};

export default Toast;

interface ToastContainerProps {
  currentToast: ToastData | null;
  onClose: () => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ currentToast, onClose }) => {
  return (
    <>
      <style>
        {`
          @keyframes toast-progress {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}
      </style>
      <div className="fixed left-1/2 top-4 z-[9999] flex w-[min(460px,calc(100vw-24px))] -translate-x-1/2 flex-col items-center gap-3 sm:top-6">
        {currentToast && (
          <Toast key={currentToast.id} {...currentToast} onClose={onClose} />
        )}
      </div>
    </>
  );
};
